import { useCallback, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { uploadFile, compareFileExtension } from 'utils/helpers'
import { Button } from 'components/common'
import { DeleteIcon } from 'components/icons'

import '../fileUploadButton/fileUpload.css'
import ConfirmImportFileModal from './confirmImportModal'

type Props = {
  supportedFileTypes?: string
  uploadApiFunction: (data: any) => Promise<any>
  setRefetch?: React.Dispatch<React.SetStateAction<boolean>>
  disabled?: boolean
  className?: string
  label?: string
}

/**
 * ### Import CSV
 *
 * by passing upload API function it will upload file into chunks
 *
 */
const DeleteStoreImportButton = ({
  supportedFileTypes = '.csv',
  uploadApiFunction,
  setRefetch,
  disabled = false,
  className = '',
  label = 'Import',
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [progress, setProgress] = useState(0)
  const [showConfirmationModal, setshowConfirmationModal] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  // To handle input change
  const handleFileUpload = useCallback(async () => {
    setshowConfirmationModal(false)
    // Will check if file exists
    if (!inputRef?.current?.files || inputRef?.current?.files?.length === 0) {
      return
    }

    const file = inputRef?.current?.files[0]

    const uploadToast = toast.loading('Uploading file...', {
      duration: Infinity,
    })

    // Will check if the extension of file matches the required extension
    if (!compareFileExtension(file, 'csv')) {
      toast.error('Invalid file format. Only CSV files are allowed.', {
        id: uploadToast,
        duration: 5000,
      })
      return resetFileUpload()
    }

    setSelectedFile(file)

    await uploadFile(inputRef?.current?.files[0], uploadApiFunction, {
      setProgress,
      onError: resetFileUpload,
      toastId: uploadToast,
      onComplete: () => {
        toast.success('File Uploaded Successfully', {
          duration: 3000,
          id: uploadToast,
        })
        setRefetch?.((pre) => !pre)
        resetFileUpload()
      },
    })
  }, [inputRef])

  //  To handle reset
  const resetFileUpload = useCallback(() => {
    inputRef.current!.value = ''
    setSelectedFile(null)
    setProgress(0)
  }, [inputRef])

  // Will be triggered on button click to explicitly trigger file input tag
  const handleSelectFile = () => {
    inputRef.current?.click()
  }

  const closeModal = () => {
    resetFileUpload()
    setshowConfirmationModal(false)
  }

  return (
    <>
      <div className="flex gap-5">
        <input
          className="hidden"
          type="file"
          onChange={() => {
            setshowConfirmationModal(true)
          }}
          accept={supportedFileTypes}
          ref={inputRef}
          disabled={disabled}
        />

        <Button
          buttonText={label}
          onClick={handleSelectFile}
          variant="custom"
          className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200 ${className}`}
          icon={<DeleteIcon />}
          iconClassName="!h-5 !w-5 text-dataTableIcon"
          loading={selectedFile ? progress > 0 && progress < 100 : false}
          disabled={disabled}
        />
      </div>
      <ConfirmImportFileModal
        closeModal={closeModal}
        isOpen={showConfirmationModal}
        onConfirm={handleFileUpload}
        file={inputRef.current?.files ? inputRef.current?.files : null}
      />
    </>
  )
}

export default DeleteStoreImportButton
